import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute, AdminRoute, PaidRoute } from "@/components/layout/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import MustChangePasswordGate from "@/components/MustChangePasswordGate";
import ErrorBoundary from "@/components/ErrorBoundary";

// Public pages
import HomePage from "@/pages/public/HomePage";
import AboutPage from "@/pages/public/AboutPage";
import HowItWorksPage from "@/pages/public/HowItWorksPage";
import PricingPage from "@/pages/public/PricingPage";
import FAQPage from "@/pages/public/FAQPage";
import ContactPage from "@/pages/public/ContactPage";
import TermsPage from "@/pages/public/TermsPage";
import PrivacyPage from "@/pages/public/PrivacyPage";
import InstructorsPage from "@/pages/public/InstructorsPage";
import InstructorsByStatePage from "@/pages/public/InstructorsByStatePage";
import InstructorSlugRouter from "@/pages/public/InstructorSlugRouter";
import EventSlugRouter from "@/pages/public/EventSlugRouter";
import PlayLocationsPage from "@/pages/public/PlayLocationsPage";

// Auth pages
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import NotFoundPage from "@/pages/public/NotFoundPage";
// Member pages
import DashboardPage from "@/pages/member/DashboardPage";
import ProfilePage from "@/pages/member/ProfilePage";
import FindPlayersPage from "@/pages/member/FindPlayersPage";
import GameRequestPage from "@/pages/member/GameRequestPage";
import MessagesPage from "@/pages/member/MessagesPage";
import SettingsPage from "@/pages/member/SettingsPage";
import SubscriptionPage from "@/pages/member/SubscriptionPage";
import PaymentSuccessPage from "@/pages/member/PaymentSuccessPage";
import EventsPage from "@/pages/member/EventsPage";
import EventDetailPage from "@/pages/member/EventDetailPage";
import PostEventPage from "@/pages/member/PostEventPage";
import EventPostSuccessPage from "@/pages/member/EventPostSuccessPage";
import MyEventsPage from "@/pages/member/MyEventsPage";
import BulkImportEventsPage from "@/pages/member/BulkImportEventsPage";
import ForumPage from "@/pages/member/ForumPage";
import ForumCategoryPage from "@/pages/member/ForumCategoryPage";
import ForumThreadPage from "@/pages/member/ForumThreadPage";
import ForumNewThreadPage from "@/pages/member/ForumNewThreadPage";
import ReferralPage from "@/pages/member/ReferralPage";
import PlayerProfilePage from "@/pages/member/PlayerProfilePage";

// Admin pages
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import UserManagementPage from "@/pages/admin/UserManagementPage";
import AdminUserProfilePage from "@/pages/admin/AdminUserProfilePage";
import AdminSubscriptionsPage from "@/pages/admin/AdminSubscriptionsPage";
import MessageModerationPage from "@/pages/admin/MessageModerationPage";
import ReportsPage from "@/pages/admin/ReportsPage";
import AdminEventsPage from "@/pages/admin/AdminEventsPage";
import AdminCharityEventsPage from "@/pages/admin/AdminCharityEventsPage";
import AdminReferralsPage from "@/pages/admin/AdminReferralsPage";
import AdminCouponsPage from "@/pages/admin/AdminCouponsPage";
import AdminSmsLogPage from "@/pages/admin/AdminSmsLogPage";
import { PromoProvider } from "@/lib/promoConfig";
import AdminModerationPage from "@/pages/admin/AdminModerationPage";
import OutageBanner from "@/components/OutageBanner";
import ImageMissingBanner from "@/components/ImageMissingBanner";
import RouteTitle from "@/components/RouteTitle";
import { OUTAGE_MODE } from "@/config/outage";

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
      <AuthProvider>
        <PromoProvider>
        <ScrollToTop />
        <RouteTitle />
        <div className="min-h-screen flex flex-col">
          {OUTAGE_MODE && <OutageBanner />}
          <Navbar />
          <ImageMissingBanner />
          <main className="flex-1">
            <Routes>
              {/* Public */}
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/terms-and-conditions" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/privacy-policy" element={<PrivacyPage />} />
              <Route path="/instructors" element={<InstructorsPage />} />
              <Route path="/instructors/:state" element={<InstructorSlugRouter />} />
              <Route path="/play-locations" element={<PlayLocationsPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/* Tolerate links where the token landed in the path (e.g.
                  email clients that turn ?token=… into /token=…) so users
                  don't end up at the 404 page. */}
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/resetpassword" element={<ResetPasswordPage />} />
              <Route path="/reset_password" element={<ResetPasswordPage />} />

              {/* Member */}
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/find-players" element={<PaidRoute><FindPlayersPage /></PaidRoute>} />
              <Route path="/game-requests/:requestId" element={<GameRequestPage />} />
              <Route path="/messages" element={<PaidRoute><MessagesPage /></PaidRoute>} />
              <Route path="/messages/:conversationId" element={<PaidRoute><MessagesPage /></PaidRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
              <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
              <Route path="/referrals" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
              <Route path="/player/:userId" element={<PlayerProfilePage />} />

              {/* Events - main page public, actions require login */}
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/post" element={<ProtectedRoute><PostEventPage /></ProtectedRoute>} />
              <Route path="/events/post-success" element={<ProtectedRoute><EventPostSuccessPage /></ProtectedRoute>} />
              <Route path="/events/my-events" element={<ProtectedRoute><MyEventsPage /></ProtectedRoute>} />
              <Route path="/events/bulk-import" element={<ProtectedRoute><BulkImportEventsPage /></ProtectedRoute>} />
              <Route path="/events/:eventId" element={<EventSlugRouter />} />

              {/* Forum - main page public, reading/posting require paid */}
              <Route path="/forum" element={<ForumPage />} />
              <Route path="/forum/new" element={<PaidRoute><ForumNewThreadPage /></PaidRoute>} />
              <Route path="/forum/:categoryId" element={<PaidRoute><ForumCategoryPage /></PaidRoute>} />
              <Route path="/forum/thread/:threadId" element={<ProtectedRoute><ForumThreadPage /></ProtectedRoute>} />

              {/* Admin */}
              <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
              <Route path="/admin/users/:userId" element={<AdminRoute><AdminUserProfilePage /></AdminRoute>} />
              <Route path="/admin/subscriptions" element={<AdminRoute><AdminSubscriptionsPage /></AdminRoute>} />
              <Route path="/admin/moderation" element={<AdminRoute><MessageModerationPage /></AdminRoute>} />
              <Route path="/admin/reports" element={<AdminRoute><ReportsPage /></AdminRoute>} />
              <Route path="/admin/events" element={<AdminRoute><AdminEventsPage /></AdminRoute>} />
              <Route path="/admin/charity-events" element={<AdminRoute><AdminCharityEventsPage /></AdminRoute>} />
              <Route path="/admin/referrals" element={<AdminRoute><AdminReferralsPage /></AdminRoute>} />
              <Route path="/admin/coupons" element={<AdminRoute><AdminCouponsPage /></AdminRoute>} />
              <Route path="/admin/sms-log" element={<AdminRoute><AdminSmsLogPage /></AdminRoute>} />
              <Route path="/admin/review-moderation" element={<AdminRoute><AdminModerationPage /></AdminRoute>} />

              {/* Catch-all 404 — must remain LAST. Without this, an
                  unmatched URL renders nothing inside <main>, leaving
                  Navbar + Footer + a blank gap, which has confused
                  users with mangled email links. */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </main>
          <Footer />
        </div>
        <Toaster />
        <MustChangePasswordGate />
        </PromoProvider>
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
